import getWording from "common/utils/wording";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-extraneous-dependencies
import Chart from "react-apexcharts";
import { useMemo } from "react";
import Icon from "components/Icon/Icon";
import fr from "apexcharts/dist/locales/fr.json";
import { Constants } from "constants/Constants";
import useAuth from "common/hooks/useAuth";
import { formatDateByScale, formatTooltipByScale } from "common/utils/formatDateByScale";

export default function OrganizationStatistics({
  data,
  startDate,
  endDate,
  scale,
}: {
  data: any;
  startDate?: Date;
  endDate?: Date;
  scale?: string;
}) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  // Pré-calcul des wordings
  const sitesWording = getWording(true, true);
  const sitesWordingLower = getWording(true, false);

  const userStats = useMemo(
    () => [
      {
        name: t("stats.nbUsers", { wording: sitesWordingLower }),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat:
          data && data.userStats && data.userStats.nbTotalUsersAtEndDate != null
            ? data.userStats.nbTotalUsersAtEndDate
            : data?.userStats?.nbTotalUsers ?? 0,
      },
      ...(Constants.ANONYMOUS_USER_ACTIVE
        ? [
            {
              name: t("stats.nbLoggedUsers"),
              icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
              stat:
                data && data.userStats && data.userStats.nbLoggedUsersAtEndDate != null
                  ? data.userStats.nbLoggedUsersAtEndDate
                  : data?.userStats?.nbLoggedUsers ?? 0,
            },
            {
              name: t("stats.nbAnonymousUsers"),
              icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
              stat:
                data && data.userStats && data.userStats.nbAnonymousUsers
                  ? data.userStats.nbAnonymousUsers
                  : 0,
            },
          ]
        : []),
      // {
      //   name: t("stats.nbDownloads"),
      //   icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
      //   stat:
      //     data && data.userStats && data.userStats.nbDownloads
      //       ? data.userStats.nbDownloads
      //       : 0,
      // },
    ],
    [data, t, sitesWordingLower]
  );

  const organizationStats = useMemo(
    () => [
      {
        name: t("stats.nbSitesActiveTotal", { wording: sitesWording }),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: `${data && data.nbActiveSites ? data.nbActiveSites : 0} / ${
          data && data.nbTotalSites ? data.nbTotalSites : 0
        }`,
      },
      {
        name: t("stats.nbPosts"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data && data.nbPosts ? data.nbPosts : 0,
      },
      {
        name: t("stats.nbThreads"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data && data.nbThreads ? data.nbThreads : 0,
      },
    ],
    [data, t, sitesWording]
  );

  const toTs = (d: string) => new Date(d).getTime();

  const usersByDate = {
    datasets: [
      {
        name: t("stats.newLoggedUsers"),
        data: data.userStats?.loggedUsersByDate?.map(
          (lu: { date: string; value: string }) => ({
            x: toTs(lu.date),
            y: lu.value,
          })
        ),
      },
      ...(Constants.ANONYMOUS_USER_ACTIVE
        ? [
            {
              name: t("stats.newAnonymousUsers"),
              data: data.userStats?.anonymousUsersByDate?.map(
                (au: { date: string; value: string }) => ({
                  x: toTs(au.date),
                  y: au.value,
                })
              ),
            },
          ]
        : []),
    ],
  };

  const usersAndroidByDate = {
    datasets: [
      {
        name: t("stats.downloads"),
        type: "column",
        data: data.userStats?.androidStats?.map(
          (au: { date: string; value: any }) => ({
            x: toTs(au.date),
            y: au.value?.downloads ?? 0,
          })
        ),
      },
      {
        name: t("stats.deletions"),
        type: "column",
        data: data.userStats?.androidStats?.map(
          (au: { date: string; value: any }) => ({
            x: toTs(au.date),
            y: au.value?.deletions ?? 0,
          })
        ),
      },
      {
        name: t("stats.totalInstallations"),
        type: "line",
        data: data.userStats?.androidStats?.map(
          (au: { date: string; value: any }) => ({
            x: toTs(au.date),
            y: au.value?.totalUsers ?? 0,
          })
        ),
      },
    ],
  };

  const usersAppleByDate = {
    datasets: [
      {
        name: t("stats.regularDownloads"),
        type: "column",
        data: data.userStats?.appleStats?.map(
          (au: { date: string; value: any }) => ({
            x: toTs(au.date),
            y: (au.value?.downloads || 0) + (au.value?.redownload || 0),
          })
        ),
      },
      {
        name: t("stats.totalDownloads"),
        type: "line",
        data: data.userStats?.appleStats?.map(
          (au: { date: string; value: any }) => ({
            x: toTs(au.date),
            y: au.value?.active_installations ?? 0,
          })
        ),
      },
    ],
  };
  return (
    <>
      {data?.userStats && (
        <>
          <dl className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
            {userStats.map((item) => (
              <div
                key={item?.name}
                className="relative bg-white pt-3 px-2 sm:pt-2 sm:px-2 shadow rounded-lg overflow-hidden"
              >
                <dt>
                  <div className="absolute bg-indigo-500 rounded-md p-3">
                    {item?.icon}
                  </div>
                  <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                    {item?.name}
                  </p>
                </dt>
                <dd className="ml-16 pb-2 flex items-baseline sm:pb-3">
                  <p className="text-2xl font-semibold text-gray-900">
                    {item?.stat}
                  </p>
                </dd>
              </div>
            ))}
          </dl>

          <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
            <div className="h-80 justify-center white-box">
              <Chart
                options={{
                  chart: {
                    id: "usersStatistics",
                    locales: [fr],
                    defaultLocale: "fr",
                  },
                  stroke: {
                    curve: "straight",
                  },
                  title: {
                    text: t("stats.usersStatistics"),
                    align: "center",
                    margin: 18,
                    style: {
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "#263238",
                    },
                  },
                  legend: {
                    position: "bottom",
                  },
                  xaxis: {
                    type: "datetime",
                    labels: {
                      datetimeUTC: false,
                      formatter: (value: string) => formatDateByScale(Number(value), scale),
                    },
                    ...(startDate && { min: new Date(startDate).getTime() }),
                    ...(endDate && { max: new Date(endDate).getTime() }),
                  },
                  tooltip: {
                    x: { formatter: (value: number) => formatTooltipByScale(value, scale) },
                  },
                  yaxis: {
                    tickAmount: 5,
                    labels: {
                      formatter: (value) => Math.round(value).toString(),
                    },
                  },
                  noData: {
                    text: "Aucune données à afficher",
                    align: "center",
                    verticalAlign: "middle",
                    offsetX: 0,
                    offsetY: 0,
                    style: {
                      fontSize: "18px",
                      color: "#263238",
                    },
                  },
                }}
                series={usersByDate.datasets}
                height="100%"
                type="line"
              />
            </div>

            {Constants.DISPLAY_STORE_STATS &&
              ((Constants.DEDICATED_APP && currentUser?.isAdmin) ||
                currentUser?.isSuperadmin) && (
                <div className="h-80 justify-center white-box">
                  <Chart
                    options={{
                      chart: {
                        id: "androidStatistics",
                        locales: [fr],
                        defaultLocale: "fr",
                      },
                      stroke: {
                        curve: "straight",
                      },
                      title: {
                        text: t("stats.androidStatistics"),
                        align: "center",
                        margin: 18,
                        style: {
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#263238",
                        },
                      },
                      legend: {
                        position: "bottom",
                      },
                      xaxis: {
                        type: "datetime",
                        labels: { datetimeUTC: false },
                        ...(startDate && { min: new Date(startDate).getTime() }),
                        ...(endDate && { max: new Date(endDate).getTime() }),
                      },
                      yaxis: [
                        {
                          tickAmount: 5,
                          labels: {
                            formatter: (value) => Math.round(value).toString(),
                          },
                          seriesName: [
                            t("stats.downloads"),
                            t("stats.deletions"),
                          ],
                        },
                        {
                          opposite: true,
                          title: { text: "Total utilisateurs" },
                          tickAmount: 5,
                          labels: {
                            formatter: (value) => Math.round(value).toString(),
                          },
                        },
                      ],
                      noData: {
                        text: "Aucune données à afficher",
                        align: "center",
                        verticalAlign: "middle",
                        offsetX: 0,
                        offsetY: 0,
                        style: {
                          fontSize: "18px",
                          color: "#263238",
                        },
                      },
                    }}
                    series={usersAndroidByDate.datasets}
                    height="100%"
                    type="line"
                  />
                </div>
              )}
          </div>
          {Constants.DISPLAY_STORE_STATS &&
            ((Constants.DEDICATED_APP && currentUser?.isAdmin) ||
              currentUser?.isSuperadmin) && (
              <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
                <div className="h-80 justify-center white-box">
                  <Chart
                    options={{
                      chart: {
                        id: "appleStatistics",
                        locales: [fr],
                        defaultLocale: "fr",
                      },
                      stroke: {
                        curve: "straight",
                      },
                      title: {
                        text: t("stats.appleStatistics"),
                        align: "center",
                        margin: 18,
                        style: {
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#263238",
                        },
                      },
                      legend: {
                        position: "bottom",
                      },
                      xaxis: {
                        type: "datetime",
                        labels: { datetimeUTC: false },
                        ...(startDate && { min: new Date(startDate).getTime() }),
                        ...(endDate && { max: new Date(endDate).getTime() }),
                      },
                      yaxis: [
                        {
                          tickAmount: 5,
                          labels: {
                            formatter: (value) => Math.round(value).toString(),
                          },
                          seriesName: [
                            t("stats.regularDownloads"),
                            t("stats.totalDownloads"),
                          ],
                        },
                      ],
                      noData: {
                        text: "Aucune données à afficher",
                        align: "center",
                        verticalAlign: "middle",
                        offsetX: 0,
                        offsetY: 0,
                        style: {
                          fontSize: "18px",
                          color: "#263238",
                        },
                      },
                    }}
                    series={usersAppleByDate.datasets}
                    height="100%"
                    type="line"
                  />
                </div>
              </div>
            )}
        </>
      )}

      <dl className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        {organizationStats.map((item) => (
          <div
            key={item?.name}
            className="relative bg-white pt-3 px-2 sm:pt-2 sm:px-2 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className="absolute bg-indigo-500 rounded-md p-3">
                {item?.icon}
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {item?.name}
              </p>
            </dt>
            <dd className="ml-16 pb-2 flex items-baseline sm:pb-3">
              <p className="text-2xl font-semibold text-gray-900">
                {item?.stat}
              </p>
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}
